import '../../../../packages/neb-lit-components/src/components/neb-text';
import '../../tables/booking/neb-table-appointments';

import { openPopup } from '@neb/popup';
import { LitElement, html, css } from 'lit';

import {
  getGuestAppointments,
  getGuestAppointmentsWithPin,
} from '../../../../packages/neb-api-client/src/appointment-api-client';
import {
  openError,
  openSuccess,
} from '../../../../packages/neb-dialog/neb-banner-state';
import { BUTTON_ROLE } from '../../../../packages/neb-lit-components/src/components/neb-button';
import { POPUP_RENDER_KEYS } from '../../../../packages/neb-popup/src/renderer-keys';
import { store } from '../../../../packages/neb-redux/neb-redux-store';
import { CSS_FONT_SIZE_CAPTION } from '../../../../packages/neb-styles/neb-variables';
import { checkInAppointment } from '../../../api-clients/appointments';
import { getAppointmentQuestionnaireBooking } from '../../../api-clients/questionnaires';
import { navigate } from '../../../store';
import {
  baseStyles,
  CSS_SPACING,
  CSS_FONT_WEIGHT_BOLD,
  CSS_FONT_SIZE_HEADER,
  CSS_COLOR_HIGHLIGHT,
} from '../../../styles';

export const ELEMENTS = {
  lookUpAppointment: { id: 'look-up-appointment' },
  createAppointment: { id: 'create-appointment' },
  table: { id: 'table' },
  exitButton: { id: 'exit-button' },
  subHeaderSelfCheckIn: { selector: '.sub-header' },
};

const CONFIG = [
  {
    key: 'start',
    label: '',
    flex: css`1 0 0`,
    mobile: true,
  },
  {
    key: 'checkInButton',
    label: '',
    flex: css`0 0 135px`,
    mobile: true,
  },
];

class NebPageGuestAppointments extends LitElement {
  static get properties() {
    return {
      __appointments: Array,
      __appointmentId: String,

      enableBooking: Boolean,
      selfCheckInInterval: Number,
      selfCheckInRedirectDelay: Number,
      model: Object,
      selfCheckInPin: String,
      layout: { type: String, reflect: true },
    };
  }

  static get styles() {
    return [
      baseStyles,
      css`
        :host {
          display: flex;
          flex-direction: column;
          height: 100%;
          width: 100%;
        }

        :host([layout='small']) .header {
          margin-left: 20px;
        }

        .table {
          margin-top: ${CSS_SPACING};
        }

        .header {
          font-size: ${CSS_FONT_SIZE_HEADER};
          color: ${CSS_COLOR_HIGHLIGHT};
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
        }

        .sub-header {
          font-size: ${CSS_FONT_SIZE_CAPTION};
          margin-top: 10px;
        }

        :host([layout='small']) .sub-header {
          padding: 0px 20px;
        }

        .button-container {
          display: flex;
        }

        .exit-button {
          margin: ${CSS_SPACING} ${CSS_SPACING} 0px auto;
          width: 135px;
        }
      `,
    ];
  }

  constructor() {
    super();

    this.__initState();
    this.__initHandlers();
  }

  __initState() {
    this.__appointments = [];
    this.__appointmentId = '';

    this.layout = '';
    this.model = {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
    };

    this.selfCheckInPin = '';
    this.selfCheckInInterval = 12;
    this.selfCheckInRedirectDelay = null;

    this.onClearPin = () => {};
  }

  __initHandlers() {
    this.__handlers = {
      checkInAppointment: async appointment => {
        this.__appointmentId = appointment.id;

        try {
          await this.__openQuestionnairePopup();
        } catch (e) {
          console.error(e);

          this.__renderErrorBanner();
        }
      },

      navigateTo: ({ name: route }) => {
        navigate(route);
      },
      navigateToCheckIn: () => {
        navigate('#/check-in');
      },
      save: async (questionnaireNarrative, noChangeInSymptoms) => {
        try {
          await this.__checkInAppointment(
            questionnaireNarrative,
            noChangeInSymptoms,
          );

          return true;
        } catch (e) {
          this.__renderErrorBanner();
          return false;
        }
      },
    };
  }

  async updated(changedProps) {
    if (changedProps.has('selfCheckInPin') && this.selfCheckInPin) {
      this.__appointments = await getGuestAppointmentsWithPin(
        this.selfCheckInPin,
      );

      this.onClearPin();
    }

    if (changedProps.has('model') && Object.values(this.model).every(v => v)) {
      this.__appointments = await getGuestAppointments(this.model);
    }
  }

  async __openQuestionnairePopup() {
    const id = this.__appointmentId;

    const questionnaire = await getAppointmentQuestionnaireBooking(id);

    if (questionnaire) {
      await openPopup(POPUP_RENDER_KEYS.SUBJECTIVE_QUESTIONNAIRE, {
        questionnaire,
        onSave: this.__handlers.save,
      });
    } else {
      await this.__checkInAppointment();
    }
  }

  __redirectHomeAfterDelay(delay) {
    const delayMilliseconds = delay * 1000;

    return setTimeout(
      () => this.__handlers.navigateTo({ name: '#/' }),
      delayMilliseconds,
    );
  }

  async __checkInAppointment(questionnaireNarrative, noChangeInSymptoms) {
    const id = this.__appointmentId;
    await checkInAppointment({
      id,
      body: {
        selfCheckInQuestionnaireNarrative: questionnaireNarrative,
        selfCheckInQuestionnaireNoChangeInSymptoms: noChangeInSymptoms,
      },
    });

    store.dispatch(openSuccess('Appointment checked in successfully'));

    if (Object.values(this.model).every(v => v)) {
      this.__appointments = await getGuestAppointments(this.model);
    } else {
      this.__appointments = this.__appointments.map(appointment => {
        if (appointment.id === id) {
          return {
            ...appointment,
            arrivedAt: true,
          };
        }

        return appointment;
      });
    }

    if (typeof this.selfCheckInRedirectDelay === 'number') {
      await this.__redirectHomeAfterDelay(this.selfCheckInRedirectDelay);
    }
  }

  __renderErrorBanner() {
    store.dispatch(
      openError('An error occurred when checking in the appointment'),
    );
  }

  __renderSelfCheckInIntervalMessage() {
    let message;

    const hours = (this.selfCheckInInterval * 5) / 60;

    switch (this.selfCheckInInterval) {
      case 6:
        message = 'up to 30 minutes before your scheduled appointment time.';
        break;
      case 12:
        message = 'up to 1 hour before your scheduled appointment time.';
        break;
      case 288:
        message = 'at anytime the same day of your appointment.';
        break;
      default:
        message = `up to ${hours} hours before your scheduled appointment time.`;
    }
    return message;
  }

  render() {
    return html`
      <span class="header">Upcoming Appointments</span>

      <neb-text class="sub-header"
        >You can check in ${this.__renderSelfCheckInIntervalMessage()}</neb-text
      >

      <neb-table-appointments
        id="${ELEMENTS.table.id}"
        class="table"
        .config="${CONFIG}"
        .layout="${this.layout}"
        .model="${this.__appointments}"
        .onCheckIn="${this.__handlers.checkInAppointment}"
        .selfCheckInInterval="${this.selfCheckInInterval}"
      >
        <neb-text slot="noItemsText">
          No appointments found. Please check for any typos or spelling errors
          and
          <neb-text
            id="${ELEMENTS.lookUpAppointment.id}"
            name="#/check-in"
            link
            .onClick="${this.__handlers.navigateTo}"
            >try again</neb-text
          >. <br />
          <br />
          If you scheduled an appointment online, please login to view your
          appointments and check in. <br />
          ${this.enableBooking
            ? html`
                <br />
                Click
                <neb-text
                  id="${ELEMENTS.createAppointment.id}"
                  name="#/book-appointment"
                  link
                  .onClick="${this.__handlers.navigateTo}"
                  >here</neb-text
                >
                to schedule an appointment.
              `
            : ''}
        </neb-text></neb-table-appointments
      >

      <div class="button-container">
        <neb-button
          id="${ELEMENTS.exitButton.id}"
          class="exit-button"
          label="Exit"
          .role="${BUTTON_ROLE.OUTLINE}"
          .onClick="${this.__handlers.navigateToCheckIn}"
        ></neb-button>
      </div>
    `;
  }
}
customElements.define('neb-page-guest-appointments', NebPageGuestAppointments);
